<template>
  <div class="border border-gray-200 rounded-xl overflow-hidden">
    <b-table-simple
      responsive
      class="!mb-0"
    >
      <b-thead class="!bg-[#F9FAFB]">
        <b-tr>
          <b-th class="!w-7 !pr-[1px]">
            <b-form-checkbox
              :checked="allChecked"
              :indeterminate="isIndeterminate"
              @change.native="onToggleAll($event.target.checked)"
            />
          </b-th>
          <b-th>Produk</b-th>
          <b-th>Stok</b-th>
          <b-th>Nominal Diskon</b-th>
          <b-th>Minimum Transaksi</b-th>
          <b-th class="!text-right !text-primary">
            <span
              class="cursor-pointer text-primary"
              @click="onReset"
            >Reset</span>
          </b-th>
        </b-tr>
      </b-thead>
      <b-tbody v-if="localRows.length === 0">
        <b-tr>
          <b-td
            colspan="6"
            class="!text-center !py-10"
          >
            <div class="text-[#828282] text-sm">
              Belum ada produk yang dipilih
            </div>
          </b-td>
        </b-tr>
      </b-tbody>
      <b-tbody
        v-for="(row, index) in localRows"
        v-else
        :key="`body-${row.id}`"
      >
        <b-tr>
          <b-td class="!align-middle !w-7 !pr-[1px]">
            <b-form-checkbox
              :checked="isParentChecked(row)"
              @change.native="onParentToggle(row, $event.target.checked)"
            />
          </b-td>
          <b-td class="!align-middle !pl-[1px]">
            <div class="flex items-center gap-2">
              <b-button
                v-if="row.hasVariant"
                variant="link"
                class="p-0 text-body w-4 h-4 inline-flex items-center justify-center"
                @click="toggleExpand(row.id)"
              >
                <b-icon :icon="row.expanded ? 'chevron-down' : 'chevron-right'" />
              </b-button>
              <span
                v-else
                class="w-4 h-4 inline-flex items-center justify-center"
              />
              <span
                class="min-w-[26px] h-5 rounded-full bg-[#ff5a43] text-white text-[11px] font-semibold inline-flex items-center justify-center px-[6px]"
                :style="{ visibility: row.hasVariant ? 'visible' : 'hidden' }"
              >{{ row.children.length || 0 }}</span>
              <b-img
                :src="row.product_image || defaultImage"
                class="w-10 h-10 object-cover rounded flex-shrink-0"
              />
              <div>
                <div class="font-medium text-[#333333] leading-snug">
                  {{ row.product_name }}
                </div>
                <div class="text-sm text-[#828282]">
                  {{ row.priceLabel }}
                </div>
              </div>
            </div>
          </b-td>
          <b-td class="!align-middle">
            {{ row.product_stock }}
          </b-td>
          <b-td class="!align-middle !min-w-[180px]">
            <template v-if="!row.hasVariant">
              <b-input-group
                :append="discountType === 'percentage' ? '%' : null"
                :prepend="discountType === 'nominal' ? 'Rp' : null"
              >
                <b-form-input
                  v-model.number="row.discount"
                  type="number"
                  min="0"
                  :max="discountType === 'percentage' ? 100 : row.priceLabel"
                  placeholder="0"
                  :disabled="!row.checked"
                  :state="row.checked && getFinalPrice(row) < 1 ? false : null"
                  :formatter="formatPositiveNumber"
                />
              </b-input-group>
              <div
                v-if="row.checked && getFinalPrice(row) < 1"
                class="text-danger text-sm"
              >
                Nominal Tidak Valid
              </div>
            </template>
          </b-td>
          <b-td class="!align-middle !min-w-[200px]">
            <b-input-group
              v-if="!row.hasVariant"
              prepend="Rp"
            >
              <b-form-input
                v-model.number="row.minTransaction"
                type="number"
                min="0"
                placeholder="0"
                :disabled="!row.checked"
                :formatter="formatPositiveNumber"
              />
            </b-input-group>
          </b-td>
          <b-td class="!text-right !align-middle">
            <b-button
              variant="link"
              class="p-0 text-danger"
              @click="onRemove(index)"
            >
              <b-icon icon="x-circle" />
            </b-button>
          </b-td>
        </b-tr>

        <b-tr
          v-for="child in (row.expanded ? (row.showAllVariants ? row.children : row.children.slice(0, 10)) : [])"
          :key="child.id"
          class="bg-white"
        >
          <b-td class="!align-middle !w-7 !pr-[1px]" />
          <b-td class="!align-middle !pl-[1px]">
            <div class="flex items-center gap-2">
              <div class="w-4 h-4 inline-flex items-center justify-center">
                <b-form-checkbox v-model="child.checked" />
              </div>
              <b-img
                :src="child.product_image || defaultImage"
                class="w-9 h-9 object-cover rounded flex-shrink-0"
              />
              <div>
                <div class="text-[#333333]">
                  {{ child.product_name }}
                </div>
                <div class="text-sm text-[#828282]">
                  {{ child.product_price | currency }}
                </div>
              </div>
            </div>
          </b-td>
          <b-td class="!align-middle">
            {{ child.product_stock }}
          </b-td>
          <b-td class="!align-middle !min-w-[180px]">
            <b-input-group
              :append="discountType === 'percentage' ? '%' : null"
              :prepend="discountType === 'nominal' ? 'Rp' : null"
            >
              <b-form-input
                v-model.number="child.discount"
                type="number"
                min="0"
                :max="discountType === 'percentage' ? 100 : child.product_price"
                placeholder="0"
                :disabled="!child.checked"
                :state="child.checked && getFinalPrice(child) < 1 ? false : null"
                :formatter="formatPositiveNumber"
              />
            </b-input-group>
            <div
              v-if="child.checked && getFinalPrice(child) < 1"
              class="text-danger text-sm"
            >
              Nominal Tidak Valid
            </div>
          </b-td>
          <b-td class="!align-middle !min-w-[200px]">
            <b-input-group prepend="Rp">
              <b-form-input
                v-model.number="child.minTransaction"
                type="number"
                min="0"
                placeholder="0"
                :disabled="!child.checked"
                :formatter="formatPositiveNumber"
              />
            </b-input-group>
          </b-td>
          <b-td class="!align-middle" />
        </b-tr>
        <b-tr v-if="row.expanded && row.children.length > 10 && !row.showAllVariants">
          <b-td
            colspan="6"
            class="!bg-white !pl-7"
          >
            <div class="py-2">
              <span
                class="text-[#FF5A43] cursor-pointer text-sm font-medium"
                @click="toggleShowAllVariants(row.id)"
              >Tampilkan {{ row.children.length - 10 }} produk lainnya</span>
            </div>
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
  </div>
</template>

<script>
export default {
  filters: {
    currency(value) {
      if (value === null || value === undefined) return '-'
      const numValue = Number(value)
      if (Number.isNaN(numValue)) return '-'
      return `Rp${numValue.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')}`
    },
  },
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    bulkPayload: {
      type: Object,
      default: null,
    },
    discountType: {
      type: String,
      default: 'percentage',
    },
  },
  data() {
    return {
      localRows: [],
      defaultImage: 'https://storage.googleapis.com/komerce/assets/svg/default-image.svg',
    }
  },
  computed: {
    selectableRows() {
      return this.localRows.flatMap(row => (row.hasVariant ? row.children : [row]))
    },
    checkedRows() {
      return this.selectableRows.filter(r => r.checked)
    },
    allChecked() {
      return this.selectableRows.length > 0 && this.selectableRows.every(r => r.checked)
    },
    isIndeterminate() {
      const checked = this.selectableRows.filter(r => r.checked).length
      return checked > 0 && checked < this.selectableRows.length
    },
    selectedCount() {
      return this.checkedRows.length
    },
    hasInvalidPrice() {
      return this.selectableRows.some(r => r.checked && this.getFinalPrice(r) < 10000)
    },
    hasPriceBelowOne() {
      return this.selectableRows.some(r => r.checked && this.getFinalPrice(r) < 1)
    },
    invalidPriceProductNames() {
      return this.selectableRows
        .filter(r => r.checked && this.getFinalPrice(r) < 10000)
        .map(r => r.product_name)
    },
  },
  watch: {
    products: {
      immediate: true,
      handler(newVal) {
        const existingParentMap = new Map(this.localRows.map(r => [r.id, r]))
        const existingMap = new Map(this.localRows.map(r => [r.id, r]))

        this.localRows = (newVal || []).map(product => {
          const hasVariant = Array.isArray(product.variants) && product.variants.length > 0
          const existingParent = existingParentMap.get(product.id)

          if (!hasVariant && existingMap.has(product.id)) {
            const existing = existingMap.get(product.id)
            return {
              ...product,
              hasVariant: false,
              showAllVariants: existing.showAllVariants || false,
              expanded: existing.expanded || false,
              checked: existing.checked || false,
              discount: existing.discount || 0,
              minTransaction: existing.minTransaction || 0,
              priceLabel: this.$options.filters.currency(product.product_price),
              children: [],
            }
          }

          if (!hasVariant) {
            return {
              ...product,
              hasVariant: false,
              expanded: false,
              showAllVariants: false,
              checked: product.checked || false,
              discount: product.discount || 0,
              minTransaction: product.minTransaction || 0,
              priceLabel: this.$options.filters.currency(product.product_price),
              children: [],
            }
          }

          const existingChildMap = new Map(
            (existingParent?.children || []).map(c => [c.id, c]),
          )

          const children = product.variants.map(variant => {
            const childId = `${product.id}-${variant.option_id || variant.variant_id || variant.product_name}`
            const existingChild = existingChildMap.get(childId)
            const base = {
              ...variant,
              id: childId,
              product_id: product.id,
            }
            if (existingChild) {
              return {
                ...base,
                checked: existingChild.checked || false,
                discount: existingChild.discount || 0,
                minTransaction: existingChild.minTransaction || 0,
              }
            }
            return {
              ...base,
              checked: variant.checked || false,
              discount: variant.discount || 0,
              minTransaction: variant.minTransaction || 0,
            }
          })

          return {
            ...product,
            hasVariant: true,
            expanded: existingParent?.expanded || false,
            showAllVariants: existingParent?.showAllVariants || false,
            checked: false,
            discount: 0,
            minTransaction: 0,
            priceLabel: `${this.$options.filters.currency(product.min_variant_price)} - ${this.$options.filters.currency(product.max_variant_price)}`,
            children,
          }
        })
        this.emitChange()
      },
    },
    bulkPayload(payload) {
      if (!payload) return
      this.localRows = this.localRows.map(row => {
        if (!row.hasVariant) {
          if (!row.checked) return row
          return {
            ...row,
            discount: Number(payload.discountValue) || 0,
            minTransaction: Number(payload.minTransaction) || 0,
          }
        }
        return {
          ...row,
          children: row.children.map(child => {
            if (!child.checked) return child
            return {
              ...child,
              discount: Number(payload.discountValue) || 0,
              minTransaction: Number(payload.minTransaction) || 0,
            }
          }),
        }
      })
      this.emitChange()
    },
    discountType(newVal) {
      if (newVal === 'percentage') {
        this.localRows = this.localRows.map(row => {
          if (!row.hasVariant) {
            return {
              ...row,
              discount: row.discount > 100 ? 100 : row.discount,
            }
          }
          return {
            ...row,
            children: row.children.map(child => ({
              ...child,
              discount: child.discount > 100 ? 100 : child.discount,
            })),
          }
        })
      }
    },
    localRows: {
      deep: true,
      handler() {
        this.emitChange()
        this.$emit('update:selected-count', this.selectedCount)
        this.$emit('update:has-invalid-price', this.hasInvalidPrice)
        this.$emit('update:has-price-below-one', this.hasPriceBelowOne)
        this.$emit('update:invalid-price-products', this.invalidPriceProductNames)
      },
    },
  },
  methods: {
    formatPositiveNumber(value) {
      if (value === '' || value === null || value === undefined) return value
      return String(value).replace(/[^0-9]/g, '')
    },
    toggleShowAllVariants(id) {
      this.localRows = this.localRows.map(row => {
        if (row.id !== id) return row
        return { ...row, showAllVariants: true }
      })
    },
    toggleExpand(id) {
      this.localRows = this.localRows.map(row => {
        if (row.id !== id) return row
        return { ...row, expanded: !row.expanded }
      })
    },
    isParentChecked(row) {
      if (!row.hasVariant) return row.checked
      if (!row.children.length) return false
      return row.children.every(child => child.checked)
    },
    onParentToggle(row, val) {
      this.localRows = this.localRows.map(item => {
        if (item.id !== row.id) return item
        if (!item.hasVariant) return { ...item, checked: val }
        return {
          ...item,
          children: item.children.map(child => ({ ...child, checked: val })),
        }
      })
    },
    onToggleAll(val) {
      this.localRows = this.localRows.map(row => {
        if (!row.hasVariant) return { ...row, checked: val }
        return {
          ...row,
          children: row.children.map(child => ({ ...child, checked: val })),
        }
      })
    },
    getFinalPrice(row) {
      const price = Number(row.product_price) || 0
      const discount = Number(row.discount) || 0
      if (this.discountType === 'percentage') {
        return price - (price * discount) / 100
      }
      // nominal
      return price - discount
    },
    onRemove(index) {
      const removed = this.localRows.splice(index, 1)
      this.$emit('remove', removed[0].id)
    },
    onReset() {
      this.localRows = []
      this.$emit('remove-all')
    },
    emitChange() {
      this.$emit('change', this.selectableRows.map(r => ({
        id: r.product_id || r.id,
        variant_id: Number(r.option_id) || null,
        product_name: r.product_name,
        product_price: r.product_price,
        discount: r.checked ? r.discount : 0,
        minTransaction: r.checked ? r.minTransaction : 0,
      })))
    },
  },
}
</script>
